/**
 * Checks if a value is an empty plain object (e.g. {}).
 * Optimized for performance:
 * 1. Fast type checks first.
 * 2. Uses for-in loop to detect properties early (avoids O(n) array creation of Object.keys).
 * 3. Prototype check last.
 */
export const isEmptyObject = (obj: unknown): boolean => {
  // 1. Fast check: Filter out null and non-objects
  if (obj === null || typeof obj !== 'object') {
    return false;
  }

  // 2. Fast check: Filter out Arrays
  if (Array.isArray(obj)) {
    return false;
  }

  // 3. Performance Optimization: Use for...in loop
  // This is faster than Object.keys(obj).length because it returns false
  // as soon as the first property is found, without iterating the whole object
  // or allocating memory for an array.
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return false;
    }
  }

  // 4. Robustness check: Ensure it's a plain object or Object.create(null)
  // Only performed if the object has no properties.
  const proto = Object.getPrototypeOf(obj);
  return proto === Object.prototype || proto === null;
};
